import { useState, useEffect, useCallback, useRef } from "react";

export interface MidiNote {
  note: number; // MIDI note number 0-127
  velocity: number;
  timestamp: number;
  channel: number;
}

export interface MidiDevice {
  id: string;
  name: string;
  manufacturer: string;
}

interface UseMidiReturn {
  isSupported: boolean;
  isConnected: boolean;
  devices: MidiDevice[];
  activeDevice: MidiDevice | null;
  activeNotes: Set<number>;
  lastNote: MidiNote | null;
  error: string | null;
  connectDevice: (deviceId: string) => void;
  onNoteOn: (callback: (note: MidiNote) => void) => void;
  onNoteOff: (callback: (note: number) => void) => void;
}

const NOTE_ON = 0x90;
const NOTE_OFF = 0x80;

export function useMidi(): UseMidiReturn {
  const [isSupported] = useState(() => !!navigator.requestMIDIAccess);
  const [isConnected, setIsConnected] = useState(false);
  const [devices, setDevices] = useState<MidiDevice[]>([]);
  const [activeDevice, setActiveDevice] = useState<MidiDevice | null>(null);
  const [activeNotes, setActiveNotes] = useState<Set<number>>(new Set());
  const [lastNote, setLastNote] = useState<MidiNote | null>(null);
  const [error, setError] = useState<string | null>(null);

  const midiAccessRef = useRef<MIDIAccess | null>(null);
  const noteOnCallbackRef = useRef<((note: MidiNote) => void) | null>(null);
  const noteOffCallbackRef = useRef<((note: number) => void) | null>(null);
  const activeInputRef = useRef<MIDIInput | null>(null);

  const onNoteOn = useCallback((callback: (note: MidiNote) => void) => {
    noteOnCallbackRef.current = callback;
  }, []);

  const onNoteOff = useCallback((callback: (note: number) => void) => {
    noteOffCallbackRef.current = callback;
  }, []);

  const handleMidiMessage = useCallback((event: MIDIMessageEvent) => {
    const data = event.data;
    if (!data || data.length < 3) return;

    const status = data[0] & 0xf0;
    const channel = data[0] & 0x0f;
    const noteNumber = data[1];
    const velocity = data[2];

    if (status === NOTE_ON && velocity > 0) {
      const midiNote: MidiNote = {
        note: noteNumber,
        velocity,
        timestamp: event.timeStamp,
        channel,
      };
      setActiveNotes((prev) => {
        const next = new Set(prev);
        next.add(noteNumber);
        return next;
      });
      setLastNote(midiNote);
      noteOnCallbackRef.current?.(midiNote);
    } else if (status === NOTE_OFF || (status === NOTE_ON && velocity === 0)) {
      setActiveNotes((prev) => {
        const next = new Set(prev);
        next.delete(noteNumber);
        return next;
      });
      noteOffCallbackRef.current?.(noteNumber);
    }
  }, []);

  const refreshDevices = useCallback(() => {
    const access = midiAccessRef.current;
    if (!access) return;
    const inputDevices: MidiDevice[] = [];
    access.inputs.forEach((input) => {
      inputDevices.push({
        id: input.id,
        name: input.name || "Unknown MIDI Device",
        manufacturer: input.manufacturer || "Unknown",
      });
    });
    setDevices(inputDevices);

    // If the active device was unplugged, disconnect it so we can fall back.
    const active = activeInputRef.current;
    if (active && (!access.inputs.has(active.id) || active.state === "disconnected")) {
      active.onmidimessage = null;
      activeInputRef.current = null;
      setActiveDevice(null);
      setIsConnected(false);
      setActiveNotes(new Set());
    }

    // Auto-connect to first device if none connected
    if (inputDevices.length > 0 && !activeInputRef.current) {
      const firstInput = access.inputs.values().next().value;
      if (firstInput) {
        firstInput.onmidimessage = handleMidiMessage;
        activeInputRef.current = firstInput;
        setActiveDevice(inputDevices[0]);
        setIsConnected(true);
      }
    }
  }, [handleMidiMessage]);

  const connectDevice = useCallback(
    (deviceId: string) => {
      const access = midiAccessRef.current;
      if (!access) return;

      // Disconnect previous
      if (activeInputRef.current) {
        activeInputRef.current.onmidimessage = null;
      }

      const input = access.inputs.get(deviceId);
      if (input) {
        input.onmidimessage = handleMidiMessage;
        activeInputRef.current = input;
        const device = devices.find((d) => d.id === deviceId);
        setActiveDevice(device || null);
        setIsConnected(true);
      }
    },
    [devices, handleMidiMessage]
  );

  useEffect(() => {
    if (!isSupported) {
      setError("Web MIDI API is not supported in this browser. Use Chrome or Edge.");
      return;
    }

    navigator
      .requestMIDIAccess({ sysex: false })
      .then((access) => {
        midiAccessRef.current = access;
        refreshDevices();
        access.onstatechange = () => refreshDevices();
      })
      .catch((err) => {
        setError(`MIDI access denied: ${err.message}`);
      });

    return () => {
      if (activeInputRef.current) {
        activeInputRef.current.onmidimessage = null;
      }
    };
  }, [isSupported, refreshDevices]);

  return {
    isSupported,
    isConnected,
    devices,
    activeDevice,
    activeNotes,
    lastNote,
    error,
    connectDevice,
    onNoteOn,
    onNoteOff,
  };
}

// Utility: MIDI note number to note name
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export function midiToNoteName(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  const name = NOTE_NAMES[midi % 12];
  return `${name}${octave}`;
}

export function noteNameToMidi(name: string): number {
  const match = name.match(/^([A-G])(#|b)?(\d+)$/);
  if (!match) return -1;
  const naturalIndex = NOTE_NAMES.indexOf(match[1]);
  if (naturalIndex < 0) return -1;
  const alter = match[2] === "#" ? 1 : match[2] === "b" ? -1 : 0;
  const octave = parseInt(match[3]);
  return (octave + 1) * 12 + naturalIndex + alter;
}
